import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { motion } from "framer-motion";
import Chat from "@/components/Chat";
import {
  HeartPulse, Utensils, Pill, Clock, CheckCircle2, Circle, Activity,
  MessageSquare, ClipboardList, Loader2, CalendarClock,
} from "lucide-react";

const fmt = (iso) => new Date(iso).toLocaleString("en-IN", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

function Checklist({ elderId }) {
  const [cl, setCl] = useState(null);
  const [schedule, setSchedule] = useState([]);
  const [vitals, setVitals] = useState({ bp: "", sugar: "", pulse: "" });
  const [savingV, setSavingV] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/sathi/checklist/${elderId}`);
    setCl(data.checklist); setSchedule(data.schedule);
    setVitals({ bp: data.checklist.vitals.bp || "", sugar: data.checklist.vitals.sugar || "", pulse: data.checklist.vitals.pulse || "" });
  };
  useEffect(() => { load(); }, [elderId]);

  const saveVitals = async () => {
    setSavingV(true);
    try { await api.post(`/sathi/checklist/${elderId}/vitals`, vitals); toast.success("Vitals logged — family notified"); load(); }
    catch { toast.error("Could not save vitals"); } finally { setSavingV(false); }
  };
  const toggleMeal = async (key) => { try { await api.post(`/sathi/checklist/${elderId}/meal`, { key }); load(); } catch { toast.error("Failed"); } };
  const toggleMed = async (key) => { try { await api.post(`/sathi/checklist/${elderId}/medication`, { key }); load(); } catch { toast.error("Failed"); } };

  if (!cl) return <div className="text-slate-400 py-8">Loading checklist…</div>;

  return (
    <div className="grid lg:grid-cols-3 gap-5">
      {/* Vitals */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <h3 className="font-head text-lg font-semibold text-[#041E26] flex items-center gap-2 mb-4"><HeartPulse className="w-5 h-5 text-[#D97706]" /> Vitals</h3>
        <div className="space-y-3">
          <div><Label className="text-xs font-semibold text-slate-600">Blood pressure</Label><Input data-testid="vitals-bp" value={vitals.bp} onChange={(e) => setVitals({ ...vitals, bp: e.target.value })} placeholder="120/80" className="mt-1 h-11 rounded-xl" /></div>
          <div><Label className="text-xs font-semibold text-slate-600">Blood sugar (mg/dL)</Label><Input data-testid="vitals-sugar" value={vitals.sugar} onChange={(e) => setVitals({ ...vitals, sugar: e.target.value })} placeholder="110" className="mt-1 h-11 rounded-xl" /></div>
          <div><Label className="text-xs font-semibold text-slate-600">Pulse (bpm)</Label><Input data-testid="vitals-pulse" value={vitals.pulse} onChange={(e) => setVitals({ ...vitals, pulse: e.target.value })} placeholder="72" className="mt-1 h-11 rounded-xl" /></div>
          <Button data-testid="save-vitals-btn" onClick={saveVitals} disabled={savingV} className="w-full h-11 rounded-full bg-[#041E26] hover:bg-[#0A2F3C] text-white gap-2">{savingV ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Activity className="w-4 h-4" /> Log vitals</>}</Button>
        </div>
      </div>

      {/* Meals + Meds */}
      <div className="space-y-5">
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <h3 className="font-head text-lg font-semibold text-[#041E26] flex items-center gap-2 mb-4"><Utensils className="w-5 h-5 text-[#D97706]" /> Meals</h3>
          <div className="space-y-2">
            {Object.entries(cl.meals).map(([meal, done]) => (
              <button key={meal} data-testid={`meal-${meal}`} onClick={() => toggleMeal(meal)} className={`w-full flex items-center gap-3 p-3 rounded-xl border text-left ${done ? "bg-green-50 border-green-200" : "border-slate-200 hover:border-slate-300"}`}>
                {done ? <CheckCircle2 className="w-5 h-5 text-green-600" /> : <Circle className="w-5 h-5 text-slate-300" />}
                <span className={`text-sm font-medium ${done ? "text-green-700 line-through" : "text-[#041E26]"}`}>{meal}</span>
              </button>
            ))}
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <h3 className="font-head text-lg font-semibold text-[#041E26] flex items-center gap-2 mb-4"><Pill className="w-5 h-5 text-[#D97706]" /> Medications</h3>
          <div className="space-y-2">
            {cl.medications.map((m) => (
              <button key={m.name} data-testid={`med-${m.name}`} onClick={() => toggleMed(m.name)} className={`w-full flex items-center gap-3 p-3 rounded-xl border text-left ${m.done ? "bg-green-50 border-green-200" : "border-slate-200 hover:border-slate-300"}`}>
                {m.done ? <CheckCircle2 className="w-5 h-5 text-green-600" /> : <Circle className="w-5 h-5 text-slate-300" />}
                <span className={`text-sm font-medium ${m.done ? "text-green-700 line-through" : "text-[#041E26]"}`}>{m.name}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Schedule */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <h3 className="font-head text-lg font-semibold text-[#041E26] flex items-center gap-2 mb-4"><Clock className="w-5 h-5 text-[#D97706]" /> Today&apos;s schedule</h3>
        <div className="space-y-3">
          {schedule.map((s) => (
            <div key={s.time} className="flex gap-3 items-start">
              <span className="font-mono-c text-xs text-[#D97706] w-12 shrink-0">{s.time}</span>
              <span className="text-sm text-slate-600">{s.item}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function SathiPortal() {
  const [elders, setElders] = useState(null);
  const [active, setActive] = useState(null);
  const [tab, setTab] = useState("checklist");

  useEffect(() => {
    api.get("/sathi/elders").then(({ data }) => { setElders(data.elders); if (data.elders[0]) setActive(data.elders[0]); }).catch(() => setElders([]));
  }, []);

  if (elders === null) return <div className="text-slate-400 py-10">Loading…</div>;
  if (elders.length === 0) return (
    <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-12 text-center">
      <p className="text-slate-500">No patients assigned yet.</p>
      <p className="text-sm text-slate-400 mt-1">Ask the family to add your mobile number / email under &quot;Assign a Sathi&quot; in their dashboard.</p>
    </div>
  );

  return (
    <div>
      {/* Elder selector */}
      <div className="flex flex-wrap gap-2 mb-6">
        {elders.map((e) => (
          <button key={e.id} data-testid={`sathi-elder-${e.id}`} onClick={() => setActive(e)}
            className={`px-4 py-2 rounded-full text-sm font-medium ${active?.id === e.id ? "bg-[#041E26] text-white" : "bg-white border border-slate-200 text-slate-600"}`}>{e.name} · {e.age} yrs</button>
        ))}
      </div>

      {active && (
        <>
          <div className="bg-white rounded-2xl border border-slate-200 p-5 mb-6 flex items-center justify-between flex-wrap gap-3">
            <div>
              <h2 className="font-head text-xl font-semibold text-[#041E26]">{active.name}</h2>
              <p className="text-sm text-slate-500">{active.plan || "—"} · {active.conditions || "No conditions listed"} · {active.city}</p>
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-600"><CalendarClock className="w-4 h-4 text-[#D97706]" /> Next visit: {active.next_visit ? fmt(active.next_visit) : "—"}</div>
          </div>

          <div className="flex gap-2 mb-5">
            <button data-testid="tab-checklist" onClick={() => setTab("checklist")} className={`px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-2 ${tab === "checklist" ? "bg-[#F59E0B] text-[#041E26]" : "bg-white border border-slate-200 text-slate-600"}`}><ClipboardList className="w-4 h-4" /> Daily Care Checklist</button>
            <button data-testid="tab-chat" onClick={() => setTab("chat")} className={`px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-2 ${tab === "chat" ? "bg-[#F59E0B] text-[#041E26]" : "bg-white border border-slate-200 text-slate-600"}`}><MessageSquare className="w-4 h-4" /> Chat with family</button>
          </div>

          {tab === "checklist" ? <Checklist elderId={active.id} /> : <div className="max-w-2xl"><Chat elderId={active.id} meRole="sathi" /></div>}
        </>
      )}
    </div>
  );
}
